import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'

interface PromptMarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  label?: string
  rows?: number
  id?: string
  /** Connects the textarea to an external field error via aria-describedby. */
  describedById?: string
}

export default function PromptMarkdownEditor({
  value,
  onChange,
  label = 'Instructions (Markdown)',
  rows = 14,
  id = 'prompt-markdown-editor',
  describedById,
}: PromptMarkdownEditorProps) {
  const [tab, setTab] = useState<'write' | 'preview'>('write')
  const tabClass = (active: boolean) =>
    `rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
      active
        ? 'bg-harbor-surface-raised text-harbor-text'
        : 'text-harbor-text-muted hover:text-harbor-text'
    }`
  const writePanelId = `${id}-write-panel`
  const previewPanelId = `${id}-preview-panel`
  const writeTabId = `${id}-write-tab`
  const previewTabId = `${id}-preview-tab`

  function handleTabKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      setTab((current) => (current === 'write' ? 'preview' : 'write'))
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-semibold text-harbor-text">
          {label}
        </label>
        <div
          role="tablist"
          aria-label={`${label} view`}
          className="flex gap-1 rounded-lg border border-harbor-border bg-harbor-surface p-0.5"
          onKeyDown={handleTabKeyDown}
        >
          <button
            type="button"
            id={writeTabId}
            role="tab"
            aria-selected={tab === 'write'}
            aria-controls={writePanelId}
            tabIndex={tab === 'write' ? 0 : -1}
            className={tabClass(tab === 'write')}
            onClick={() => setTab('write')}
          >
            Write
          </button>
          <button
            type="button"
            id={previewTabId}
            role="tab"
            aria-selected={tab === 'preview'}
            aria-controls={previewPanelId}
            tabIndex={tab === 'preview' ? 0 : -1}
            className={tabClass(tab === 'preview')}
            onClick={() => setTab('preview')}
          >
            Preview
          </button>
        </div>
      </div>
      {tab === 'write' ? (
        <div id={writePanelId} role="tabpanel" aria-labelledby={writeTabId}>
          <textarea
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            {...(describedById ? { 'aria-describedby': describedById } : {})}
            className="mt-2 w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 font-mono text-sm text-harbor-text"
          />
        </div>
      ) : (
        <div
          id={previewPanelId}
          role="tabpanel"
          aria-labelledby={previewTabId}
          className="prose prose-invert mt-2 max-h-96 min-h-40 overflow-auto rounded-lg border border-harbor-border bg-harbor-surface-raised/60 px-4 py-3 text-sm text-harbor-text-secondary"
        >
          {value.trim().length === 0 ? (
            <p className="text-xs text-harbor-text-muted">Nothing to preview yet.</p>
          ) : (
            <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{value}</ReactMarkdown>
          )}
        </div>
      )}
    </div>
  )
}
